import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Button,
    Typography,
    Snackbar,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    IconButton,
    Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import Header from '../Header';
import { local_url, local_signage } from '../Const';

// Field casing isn't confirmed for this endpoint's response, so check the common variants.
const getBranchId = (branch) => branch?.BranchId || branch?.branchId || branch?.BranchID || branch?.branchID || '';
const getBranchName = (branch) => branch?.BranchName || branch?.branchName || getBranchId(branch);

const getRowId = (row) => row?.Uniqueid || row?.uniqueid || row?.uniqueId || row?.UniqueId || row?.UniqueID;
const getProposedLimit = (row) => row?.PropsedLimit ?? row?.propsedLimit ?? row?.ProposedLimit ?? row?.proposedLimit;
const getProposedDays = (row) => row?.PropsedDaysLimit ?? row?.propsedDaysLimit ?? row?.ProposedDaysLimit ?? row?.proposedDaysLimit;

export const LCApprovals = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const CREATED_USER_CODE = '10000101';

    // Branch filter dropdown
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [filterBranchId, setFilterBranchId] = useState('ALL');

    // List
    const [rows, setRows] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Approve/Reject in-flight state, keyed by row id
    const [actioningId, setActioningId] = useState(null);

    // Manager-adjusted approval values, keyed by row id: { limit, days }.
    // A row with no entry here is approved at the proposed values as-is.
    const [approvedValues, setApprovedValues] = useState({});

    // Which cell is open for editing: { rowId, field: 'limit' | 'days' }
    const [editingCell, setEditingCell] = useState(null);
    const [draftValue, setDraftValue] = useState('');

    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    const handleAuthError = () => {
        showNotification('Session expired. Please log in again.', 'error');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/Login';
    };

    // Load the Branch dropdown once, on mount
    useEffect(() => {
        const fetchBranches = async () => {
            setLoadingBranches(true);
            try {
                const response = await fetch(
                    `${local_signage}/api/v1/fetchbranchbyareabizsegment?AreaId=ALL&BizSegmentCode=LAZIZ`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.status === 401) { handleAuthError(); return; }
                if (!response.ok) throw new Error('Failed to fetch branches');
                const data = await response.json();
                const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
                setBranches(list);
            } catch (error) {
                console.error('Error fetching branches:', error);
                showNotification('Could not load the Branch list.', 'error');
            } finally {
                setLoadingBranches(false);
            }
        };
        fetchBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the LC Approvals list, filtered by Branch.
    // NOTE: endpoint to be provided by the backend team — placeholder used for now.
    // Expected response: an array (or { data: [...] }) of objects with
    // CustId, ForBranchId, CurrentLimit, CurrentDaysLimit, SFAreaId, BranchName,
    // CustomerName, PricingSchemeType, SFAreaName, PropsedLimit, PropsedDaysLimit, uniqueid.
    const fetchList = useCallback(async () => {
        setLoadingList(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/fetchlocalcreditcustomerapprovalrequests?BranchId=${filterBranchId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.status === 401) { handleAuthError(); return; }
            if (!response.ok) throw new Error('Failed to fetch LC approvals list');
            const data = await response.json();
            const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            setRows(list);
        } catch (error) {
            console.error('Error fetching LC approvals list:', error);
            showNotification('Could not load the LC Approvals list.', 'error');
            setRows([]);
        } finally {
            setLoadingList(false);
        }
    }, [filterBranchId]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const resolveBranchName = (branchId) => {
        const match = branches.find((b) => getBranchId(b) === branchId);
        return match ? getBranchName(match) : branchId;
    };

    // Effective value sent as ApprovedCreditLimit / ApprovedCreditDays:
    // the manager's edit if there is one, otherwise the proposed value.
    const getApprovedLimit = (row) => approvedValues[getRowId(row)]?.limit ?? getProposedLimit(row);
    const getApprovedDays = (row) => approvedValues[getRowId(row)]?.days ?? getProposedDays(row);

    const startEdit = (row, field) => {
        const current = field === 'limit' ? getApprovedLimit(row) : getApprovedDays(row);
        setEditingCell({ rowId: getRowId(row), field });
        setDraftValue(current == null ? '' : String(current));
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setDraftValue('');
    };

    const commitEdit = () => {
        if (!editingCell) return;
        const { rowId, field } = editingCell;
        const parsed = Number(draftValue);
        if (draftValue.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
            showNotification('Please enter a valid non-negative number.', 'error');
            return;
        }
        setApprovedValues((prev) => ({
            ...prev,
            [rowId]: { ...prev[rowId], [field]: parsed }
        }));
        cancelEdit();
    };

    // Proposed Credit Limit / Days cell: shows the value with an inline edit button.
    // Editing it sets ApprovedCreditLimit / ApprovedCreditDays for the submitted request.
    const renderEditableCell = (row, field, disabled) => {
        const rowId = getRowId(row);
        const proposed = field === 'limit' ? getProposedLimit(row) : getProposedDays(row);
        const effective = field === 'limit' ? getApprovedLimit(row) : getApprovedDays(row);
        const isEditing = editingCell?.rowId === rowId && editingCell?.field === field;
        const isChanged = effective !== proposed;

        if (isEditing) {
            return (
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                            size="small"
                            type="number"
                            autoFocus
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') cancelEdit();
                            }}
                            inputProps={{ min: 0 }}
                            sx={{ width: 110 }}
                        />
                        <IconButton size="small" color="success" onClick={commitEdit}>
                            <DoneIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={cancelEdit}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </TableCell>
            );
        }

        return (
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: isChanged ? 700 : 400 }}>
                        {effective}
                    </Typography>
                    <Tooltip title={field === 'limit' ? 'Edit approved credit limit' : 'Edit approved credit days'}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={() => startEdit(row, field)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </TableCell>
        );
    };

    const updateApprovalRequest = async (row, approved, remarks) => {
        const rowId = getRowId(row);
        setActioningId(rowId);
        try {
            if (!rowId) {
                console.error('LC approval action missing data:', { row, rowId });
                throw new Error('Missing UniqueId — cannot submit this action.');
            }
            const approvedCreditLimit = Number(getApprovedLimit(row)) || 0;
            const approvedCreditDays = Number(getApprovedDays(row)) || 0;

            const params = new URLSearchParams({
                UniqueId: rowId,
                Approved: String(approved),
                ApprovedCreditLimit: String(approvedCreditLimit),
                ApprovedCreditDays: String(approvedCreditDays),
                Remarks: remarks || '',
                CreatedUserCode: CREATED_USER_CODE
            });
            const response = await fetch(
                `${local_signage}/api/v1/updatelocalcreditcustomerapprovalrequest?${params.toString()}`,
                {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        UniqueId: rowId,
                        Approved: approved,
                        ApprovedCreditLimit: approvedCreditLimit,
                        ApprovedCreditDays: approvedCreditDays,
                        Remarks: remarks,
                        CreatedUserCode: CREATED_USER_CODE
                    })
                }
            );
            if (response.status === 401) { handleAuthError(); return; }
            if (!response.ok) {
                let detail = '';
                try {
                    const errBody = await response.clone().json();
                    detail = errBody?.message || errBody?.error || errBody?.title || JSON.stringify(errBody);
                } catch {
                    detail = await response.text();
                }
                console.error('Update approval request failed:', response.status, detail);
                throw new Error(detail || 'Failed to update approval request');
            }
            showNotification(approved ? 'Approved successfully.' : 'Rejected successfully.', 'success');
            fetchList();
        } catch (error) {
            console.error('Error updating LC approval request:', error);
            showNotification(error.message || (approved ? 'Could not approve this request.' : 'Could not reject this request.'), 'error');
        } finally {
            setActioningId(null);
        }
    };

    const handleApprove = (row) => updateApprovalRequest(row, true, 'Approved by manager');

    const handleReject = (row) => updateApprovalRequest(row, false, 'Rejected by manager');

    return (
        <>
            <Header />
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardHeader
                        avatar={<FactCheckIcon sx={{ color: '#004d00' }} />}
                        title={
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d00' }}>
                                Local Credit Customer Approval
                            </Typography>
                        }
                    />
                    <CardContent>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Branch</InputLabel>
                                    <Select
                                        label="Branch"
                                        value={filterBranchId}
                                        onChange={(e) => setFilterBranchId(e.target.value)}
                                        disabled={loadingBranches}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {branches.map((branch) => (
                                            <MenuItem key={getBranchId(branch)} value={getBranchId(branch)}>
                                                {getBranchName(branch)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                                        <TableCell>Customer ID</TableCell>
                                        <TableCell>Customer Name</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell>Proposed By</TableCell>
                                        <TableCell>Proposed On</TableCell>
                                        <TableCell>Current Credit Limit</TableCell>
                                        <TableCell>Current Credit Days</TableCell>
                                        <TableCell>Proposed Credit Limit</TableCell>
                                        <TableCell>Proposed Credit Days</TableCell>
                                        <TableCell align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loadingList ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                                <CircularProgress size={22} />
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                No records found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((row, index) => {
                                            const rowId = getRowId(row) || `${row.CustId}-${row.ForBranchId}`;
                                            const custId = row.CustId || row.custId;
                                            const branchId = row.ForBranchId || row.forBranchId;
                                            const isActioning = actioningId === rowId;
                                            return (
                                                <TableRow
                                                    key={`${rowId}-${index}`}
                                                    hover
                                                    sx={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#e8f5e9' }}
                                                >
                                                    <TableCell>{custId}</TableCell>
                                                    <TableCell>{row.CustomerName || row.customerName}</TableCell>
                                                    <TableCell>{row.BranchName || row.branchName || resolveBranchName(branchId)}</TableCell>
                                                    <TableCell>{row.ProposedBy || row.proposedBy}</TableCell>
                                                    <TableCell>{row.ProposedOn || row.proposedOn}</TableCell>
                                                    <TableCell>{row.CurrentLimit ?? row.currentLimit}</TableCell>
                                                    <TableCell>{row.CurrentDaysLimit ?? row.currentDaysLimit}</TableCell>
                                                    {renderEditableCell(row, 'limit', isActioning)}
                                                    {renderEditableCell(row, 'days', isActioning)}
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<CheckCircleIcon />}
                                                                disabled={isActioning}
                                                                onClick={() => handleApprove(row)}
                                                                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<CancelIcon />}
                                                                disabled={isActioning}
                                                                onClick={() => handleReject(row)}
                                                                sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#8e0000' } }}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default LCApprovals;
